import React from 'react'

import { object, string, bool, arrayOf, shape } from 'prop-types'

import i18n from 'Pods/Shared/I18n'

import { EMPTY_ARRAY, EMPTY_OBJECT } from 'Utils/stableDefaults'

import GenerationToast from './Components/GenerationToast/GenerationToast'
import Hamburger from './Components/Hamburger/Hamburger'
import Logo from './Components/Logo/Logo'
import Menu, { CREATE_AI_ID } from './Components/Menu/Menu'
import Search from './Components/Search/Search'
import UserActions, { VISIBLE_ACTION_IDS } from './Components/UserActions/UserActions'
import { useHamburgerStatus } from './Hooks/useHamburgerStatus'
import { NavOverlayProvider, useNavOverlay, NAV_OVERLAY_IDS } from './Hooks/useNavOverlay'

function NavigationBar({
  commonRoutes,
  mainMenuItems = EMPTY_OBJECT,
  communityLinks = EMPTY_ARRAY,
  createAiLinks = EMPTY_ARRAY,
  createAiGeneration = EMPTY_OBJECT,
  showFreeTrialTag = false,
  actionsLinks = EMPTY_ARRAY,
  actionsConfig = EMPTY_OBJECT,
  locale = 'en',
  isInListingPage = false,
  searchInputValue = '',
  searchPlaceholder,
  searchDropdown = null,
  currentUserId = null,
  assetCategories = EMPTY_OBJECT,
  printableCategories = EMPTY_OBJECT,
  byType = EMPTY_ARRAY,
  budgetsData = EMPTY_OBJECT,
}) {
  const { root } = commonRoutes
  const { isActive, toggle } = useHamburgerStatus()
  const { openId, close } = useNavOverlay()
  const t = (key) => i18n.t(key, { ns: 'rails', lng: locale })

  // Same server-translated label the Create with AI tag renders (createAiLinks[].tag),
  // reused for the user-menu Subscription pill so both read one source. Client i18n is
  // intentionally not used here - these nav labels are translated server-side.
  const freeTrialLabel = createAiLinks.find((link) => link.tag)?.tag
  // Same backend item the Create with AI menu trigger renders (href + title + track
  // name), reused by the mobile-only generation-state action in the actions bar.
  const createAiItem = Object.values(mainMenuItems).find((item) => item?.id === CREATE_AI_ID)

  // The dimming backdrop is shared by the mobile overlays - the hamburger menu, the
  // (full-screen) user menu and the cart / inbox / notifications action popovers. CSS
  // hides it at md+, so it only paints on mobile; clicking it closes whichever overlay
  // is currently open.
  const isBackdropVisible = isActive
    || openId === NAV_OVERLAY_IDS.USER_MENU
    || VISIBLE_ACTION_IDS.includes(openId)

  function handleBackdropClose() {
    close()
  }

  return (
    <div className="unified-nav">
      {isBackdropVisible && (
        <button
          type="button"
          className="unified-nav__backdrop"
          onClick={handleBackdropClose}
          aria-label={t('navigation.aria.close_menu')}
        />
      )}
      <Hamburger isActive={isActive} onToggle={toggle} locale={locale} />
      <Logo url={root} locale={locale} />
      <Search
        locale={locale}
        isInListingPage={isInListingPage}
        searchInputValue={searchInputValue}
        searchPlaceholder={searchPlaceholder}
        searchDropdown={searchDropdown}
      />
      <Menu
        isActive={isActive}
        mainMenuItems={mainMenuItems}
        communityLinks={communityLinks}
        createAiLinks={createAiLinks}
        createAiGeneration={createAiGeneration}
        showFreeTrialTag={showFreeTrialTag}
        locale={locale}
        currentUserId={currentUserId}
        assetCategories={assetCategories}
        printableCategories={printableCategories}
        byType={byType}
        budgetsData={budgetsData}
        actionsConfig={actionsConfig}
      />
      <UserActions
        actionsLinks={actionsLinks}
        actionsConfig={actionsConfig}
        locale={locale}
        showFreeTrialTag={showFreeTrialTag}
        freeTrialLabel={freeTrialLabel}
        createAiItem={createAiItem}
        createAiGeneration={createAiGeneration}
        currentUserId={currentUserId}
      />
      <GenerationToast labels={createAiGeneration} currentUserId={currentUserId} />
    </div>
  )
}

// The overlay coordinator wraps the whole bar so the hamburger, primary-menu
// dropdowns, action popovers and user menu share one "which overlay is open" value.
function Navigation(props) {
  return (
    <NavOverlayProvider>
      <NavigationBar {...props} />
    </NavOverlayProvider>
  )
}

NavigationBar.propTypes = {
  commonRoutes: object.isRequired,
  mainMenuItems: object,
  communityLinks: arrayOf(shape({
    href: string,
    trackText: string,
    title: string,
    icon: string,
  })),
  createAiLinks: arrayOf(shape({
    id: string,
    href: string,
    title: string,
    trackText: string,
    icon: string,
    tag: string,
  })),
  createAiGeneration: shape({
    generatingTitle: string,
    readyTitle: string,
    openModel: string,
    errorTitle: string,
    errorHref: string,
    dismiss: string,
    multipleGenerationsEnabled: bool,
  }),
  showFreeTrialTag: bool,
  actionsLinks: arrayOf(shape({
    id: string,
    href: string,
    icon: string,
  })),
  actionsConfig: shape({
    isCurrentUser: bool,
    newRoyaltyUi: bool,
  }),
  locale: string,
  isInListingPage: bool,
  searchInputValue: string,
  searchPlaceholder: string.isRequired,
  searchDropdown: object,
  currentUserId: string,
  assetCategories: object,
  printableCategories: object,
  byType: arrayOf(shape({
    icon: string,
    name: string,
    path: string,
  })),
  budgetsData: shape({
    assetsBaseUrl: string,
    printableBaseUrl: string,
    assetsBudgets: arrayOf(shape({
      title: string,
      path: string,
      icon: string,
      withSub: bool,
    })),
    printableAssetsBudgets: arrayOf(shape({
      title: string,
      path: string,
      icon: string,
      withSub: bool,
    })),
  }),
}

Navigation.propTypes = NavigationBar.propTypes

export default Navigation
